import * as React from 'react';
import { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { RegularProduct } from '@bepure/tarkin/dist/utils/cart';
import { fetchBigCommerceApi, HTTP_METHOD } from 'utils/bigCommerceAuth';
import ProductCard from './ProductCard';
import { Variants } from './ProductInformationWrapper';

declare const BASE_API_URL: string;
declare const RECHARGE_PRODUCT_DATA: any;

interface Product extends Partial<RegularProduct> {
    unit_price: number;
    sale_price?: number;
    retail_price?: number; 
    categories: Category[];
    isInStock: boolean;
    reviewSummary?: ProductReview;
}

interface Props {
    products: Product[];
    categories?: Category[];
}

const ProductSection = (props: Props) => {

    const getURLCategoryFilterId = () => {
        const param = new URLSearchParams(window.location.search).get('filter');
        if (param){
            return parseInt(param);
        }
        return;
    }

    const urlFilter = getURLCategoryFilterId();
    const [currentCategory, setCurrentCategory] = useState<number|undefined>(urlFilter ? urlFilter : props.categories ? props.categories[0].entityId : undefined);
    // const [subscriptionFrequency, setSubscriptionFrequency] = useState(0);

    const currentProducts = currentCategory === undefined
        ? props?.products
        : props.products.filter(
            p => p.categories.map(c => c.entityId).includes(currentCategory || 0)
        );

    const [previousScrollPosition, setPreviousScrollPos] = useState(0);
    const [currentScrollPosition, setCurrentScrollPos] = useState(0);
    const scrollUp = previousScrollPosition < currentScrollPosition && currentScrollPosition > (80 + 80);
    useEffect(() =>{
        window.addEventListener('scroll', handleScroll)
    })

    const handleScroll = (event: any) => {
        const scrollTop = window.pageYOffset;
        const prevPos = currentScrollPosition;
        setCurrentScrollPos(scrollTop);
        setPreviousScrollPos(prevPos);
    }
    
    const onClickCategory = (id?: number) => {
        return (e: React.MouseEvent) => {
            setCurrentCategory(id);
        };
    }

    return (
        <>
            { (props.categories?.length || 0) > 1 
            
                && 
                <>
                <div className="categories-section-buffer"></div>
                <div className={`products-section__categories-section ${scrollUp ? 'products-section__categories-section--scroll_up' : '' }`}>
                    <div className="products-section__categories-section__container">
                        <div className="products-section__categories-section__container__filter-label">Filter By:</div>
                        <ul className="products-section__categories-section__container__categories">
                            {
                                props.categories?.map((category: Category) => 
                                    <li className={`products-section__categories-section__container__categories__category ${ currentCategory === category.entityId ? 'products-section__categories-section__container__categories__category--selected': '' }`} onClick={onClickCategory(category.entityId)}>{category.name}</li>   
                                )
                            }
                        </ul>
                    </div>
                </div>
                </>
            }
            <div className="products-panel">
                <div className="products-panel__grid">
                {
                    currentProducts.map((product: Product) => 
                    
                        <ProductCard product={product}></ProductCard>
                    )
                }
               
                </div>
            </div>
        </>
    )
}

export default function render() {
    const sections = document.getElementsByClassName('products-section');
    Array.prototype.map.call(sections, (section: HTMLElement) => {
        Promise.all([getProducts(), getCategories()])
            .then((values) => {
                const products = (values[0] as Product[]);
                const categories = (values[1] as Category[]);
                if (products.length < 1) {
                    throw products;
                }
                return ReactDOM.render(
                    <ProductSection products={products} categories={categories}/>,
                    section,
                );
            })
            .catch((error: ProductError) => { console.log(error); });
    });
}

export interface ProductReview {
    review_count: number;
    score: number;
}
export interface ProductReviewResponse {
    data: {
        [sku: string]: ProductReview
    }
}

const fetchProductReviewStats = async () => {
    const response = await fetch(`${BASE_API_URL}/v1/service/product-management/products/reviews`, {
        method: 'GET',
    })
    try {
        const reviews: ProductReviewResponse = await response.json();
        return Promise.resolve(reviews);
    } catch (error) {
        return Promise.reject({
            error: error.message,
        });
    }
}

interface ProductError {
    error: string;
}
const fetchProducts = async (cursor?: string, currencyCode: string = 'NZD', direction: 'before' | 'after' = 'after'): Promise<ProductResponse | ProductError> => {
    const response = await fetchBigCommerceApi(HTTP_METHOD.POST, new Headers(), {
        variables: {
            pageSize: 12,
            cursor,
            currencyCode: currencyCode,
        },
        query: `
        query paginateProducts(
          $pageSize: Int
          $cursor: String
          $currencyCode: currencyCode
        ) {
          site {
            products (first: $pageSize, ${direction}:$cursor) {
              pageInfo {
                startCursor
                endCursor
                hasNextPage
                hasPreviousPage
              }
              edges {
                cursor
                node {
                  entityId 
                  sku
                  name
                  description
                  plainTextDescription
                  images {
                    edges {
                     node {
                    ...ImageFields 
                      
                    }
                    }
                  }
                  variants {
                    edges {
                      node {
                        entityId,
                        
                      }
                    }
                  },
                  prices (currencyCode: $currencyCode, includeTax: true) {
                    price {
                      ...MoneyFields
                    }
                    salePrice {
                      ...MoneyFields
                    }
                    basePrice {
                      ...MoneyFields
                    }
                    retailPrice {
                      ...MoneyFields
                    }
                    saved {
                      ...MoneyFields
                    }
                  }
                  categories {
                    edges {
                      node {
                        ...CategoryFields
                      }
                    }
                  }
                  inventory {
                    isInStock
                  }
                }
              }
            }
          }
        }
        fragment CategoryFields on Category {
          entityId
          name
          path
        }
        fragment ImageFields on Image {
          url330wide: url(width: 330)
          url660wide: url(width: 660)
          isDefault
        }
        fragment MoneyFields on Money {
          value
          currencyCode
        }
        `
    });

    try {
        const content = await response.json();
        return Promise.resolve(content);
    } catch (error) {
        return Promise.reject({
            error: error.message,
        });
    }
};

const getProducts = async (cursor?: string, productList: Product[] = [], count: number = 0): Promise<Product[] | ProductError> => {
    // todo: pagination
    // in mean time: currently recursivly fetches the paginated products, to adapt to pagination simply remove the recursion 
    // and call getProducts relative to the direction of pagination and stored "latest" cursor (in local state)
    const reviews = await fetchProductReviewStats();
    const response = await fetchProducts(cursor, localStorage.currency);
    if (response.hasOwnProperty('data')) {
        count++;
        if (count > 99) {
            return Promise.resolve(productList)
        }
        let resp: ProductResponse = (response as ProductResponse);
        productList.concat(mapProductData(resp, reviews))
        let newProductList = Array.prototype.concat(productList, mapProductData(resp, reviews));
        if (resp.data.site.products.pageInfo.hasNextPage) {
            return getProducts(resp.data.site.products.pageInfo.endCursor, newProductList, count)
        } else {
            return newProductList
        }
    }
    return Promise.reject({ event: 'getProducts failed', message: response })
}

const fetchCategories = async () => {
    const response = await fetchBigCommerceApi(HTTP_METHOD.POST, new Headers(), {
        variables: {
        },
        query: `
        query CategoryTree {
            site {
              categoryTree {
                ...CategoryFields
                
              }
            }
          }
          
          fragment CategoryFields on CategoryTreeItem {
            name
            path
            entityId
          }
        `
    });

    try {
        const content = await response.json();
        return Promise.resolve(content);
    } catch (error) {
        return Promise.reject({
            error: error.message,
        });
    }
}

const getCategories = async () => {
    const response: CategoryResponse = await fetchCategories();
    if (response.hasOwnProperty('data')) {
        return response.data.site.categoryTree;
    }
    return Promise.reject({ event: 'getCategories failed', message: response })
}

interface ImageEdge {
    node: {
        url330wide: string;
        url660wide: string;
        isDefault: boolean;
    }
}

interface Category {
    entityId: number;
    name: string;
    path: string;
}

interface CategoryEdge {
    node: Category;
}

interface Money {
    value: number;
    currencyCode: string;
}

interface ProductEdge {
    cursor: string;
    node: {
        entityId: number;
        sku: string;
        name: string;
        description: string;
        plainTextDescription: string;
        variants: Variants
        images: {
            edges: ImageEdge[];
        }
        prices: {
            price: Money;
            salePrice: Money;
            basePrice: Money;
            retailPrice: Money;
            saved: Money;
        }
        categories: {
            edges: CategoryEdge[];
        }
        inventory: {
            isInStock: boolean;
        }
    }
}

interface ProductResponse {
    data: {
        site: {
            products: {
                pageInfo: {
                    startCursor: string;
                    endCursor: string;
                    hasNextPage: boolean;
                    hasPreviousPage: boolean;
                }
                edges: ProductEdge[];
            }
        }
    }
}

interface CategoryResponse {
    data: {
        site: {
            categoryTree: {
                name: string;
                path: string;
                entityId: number;
            }[]
        }
    }
}

const getDefaultImage = (imageList: ImageEdge[]) => {
    return imageList.reduce((prev: string, curr: ImageEdge) => {
        return curr.node.isDefault ? curr.node.url660wide : prev;
    }, imageList[0].node.url660wide || '');
}

const mapProductData = (response: ProductResponse, reviewResponse: ProductReviewResponse): Product[] => {
    console.log("mapproductdata ",response.data.site)
    return response.data.site.products.edges.map((productEdge: ProductEdge): Product => {
        return {
            name: productEdge.node.name,
            unit_price: productEdge.node.prices.price.value,
            retail_price: productEdge.node.prices.retailPrice?.value,
            sale_price: productEdge.node.prices.salePrice?.value,
            description: productEdge.node.plainTextDescription,
            imageUrl: getDefaultImage(productEdge.node.images.edges),
            categories: productEdge.node.categories.edges.map(edge => edge.node),
            sku: productEdge.node.sku,
            isInStock: productEdge.node.inventory.isInStock,
            reviewSummary: reviewResponse.data[productEdge.node.sku],
            productId: productEdge.node.entityId,
            variantId: productEdge.node.variants.edges.values().next().value.node.entityId
        }
    })
}

